import React, { useEffect, useState } from "react";
import { FiX, FiLayers, FiArrowLeft, FiLink, FiPlusCircle, FiZap } from "react-icons/fi";
import { toast } from "react-toastify";
import { createClassGroup, extractApiErrorMessage } from "../../utils/classGroupApi";
import { DEFAULT_ARM_LABEL, pluralize } from "../../utils/classGroupHelpers";

// Single entry point for creating a class group. It first asks whether the arms already
// exist, because grouping existing classes and creating brand-new arms are different
// flows — picking the wrong one used to leave schools with duplicate classes.
const CreateClassGroupModal = ({
  isOpen,
  onClose,
  schoolId,
  onSuccess, // (createdGroup, classesToPreselect) => void
  onChooseCreateNew, // admin wants to create brand-new arms instead
  suggestions = [], // [{ base, classes }] detected from existing class names
  classGroups = [], // existing groups — reused by name instead of hitting a duplicate-name error
  subclassLabel = DEFAULT_ARM_LABEL,
}) => {
  const [step, setStep] = useState("choose"); // 'choose' | 'name'
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const armsLabel = pluralize(subclassLabel, 2).toLowerCase();

  useEffect(() => {
    if (!isOpen) return;
    setStep("choose");
    setName("");
    setError("");
  }, [isOpen]);

  if (!isOpen) return null;

  const createGroup = async (groupName, classesToPreselect = []) => {
    // A group with this name may already exist (e.g. left empty after the admin closed the
    // arm picker last time) — continue with it rather than failing on a duplicate name.
    const existing = classGroups.find(
      (g) => (g.name || "").trim().toLowerCase() === groupName.trim().toLowerCase(),
    );
    if (existing) {
      if (onSuccess) onSuccess(existing, classesToPreselect);
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError("");
      const createdGroup = await createClassGroup(schoolId, groupName.trim());
      toast.success(`Class group "${createdGroup?.name || groupName}" created`);
      if (onSuccess) onSuccess(createdGroup, classesToPreselect);
      onClose();
    } catch (err) {
      console.error("Error creating class group:", err);
      setError(extractApiErrorMessage(err, "Failed to create class group"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Give the class group a name");
      return;
    }
    createGroup(name);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiLayers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Create a class group</h3>
              <p className="text-xs text-slate-500">
                One class level (e.g. JSS 1) and its {armsLabel} (JSS 1A, JSS 1B…)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {error}
            </div>
          )}

          {step === "choose" ? (
            <>
              <p className="text-sm font-medium text-slate-800">
                Do the {armsLabel} for this level already exist on your Classes page?
              </p>

              {suggestions.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3.5 space-y-2">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                    <FiZap className="w-3.5 h-3.5" /> Suggested from your class names
                  </p>
                  {suggestions.map((s) => (
                    <button
                      key={s.base}
                      type="button"
                      disabled={loading}
                      onClick={() => createGroup(s.base, s.classes)}
                      className="w-full text-left rounded-lg border border-emerald-200 bg-white px-3 py-2 hover:border-emerald-400 disabled:opacity-60 transition-colors"
                    >
                      <span className="block text-sm font-semibold text-slate-900">
                        Group {s.base}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {s.classes.map((c) => c.class_name).join(", ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setStep("name")}
                  className="text-left rounded-xl border border-slate-200 p-4 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
                >
                  <FiLink className="w-5 h-5 text-indigo-600" />
                  <span className="mt-2 block text-sm font-semibold text-slate-900">
                    Yes, they exist
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Group classes you already have. Students and fees stay as they are.
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    if (onChooseCreateNew) onChooseCreateNew();
                  }}
                  className="text-left rounded-xl border border-slate-200 p-4 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
                >
                  <FiPlusCircle className="w-5 h-5 text-indigo-600" />
                  <span className="mt-2 block text-sm font-semibold text-slate-900">
                    No, create them
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Create a new class level and all its {armsLabel} in one go.
                  </span>
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="group_name" className="block text-sm font-medium text-slate-700">
                  What is the class level called?
                </label>
                <input
                  id="group_name"
                  type="text"
                  autoFocus
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder="e.g. Primary 1 or JSS 1"
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Next, you'll pick which of your classes are its {armsLabel}.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setStep("choose")}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FiArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  )}
                  Continue
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateClassGroupModal;
